import { apiClient, apiEndpoint } from './api.services';
import { API } from '../constants/api.constants';

export interface SubBomAttributeEnvelope<TData = unknown> {
  code: number;
  message: string;
  data: TData;
}

export type SubBomAttributeType = 'text' | 'dropdown' | 'multi-dropdown';

export interface SubBomAttributeOption {
  _id?: string | null;
  keyName: string;
  seqNumber?: number;
  isActive?: boolean;
}

export interface SubBomTextFieldConfig {
  charLimit?: number;
  isEditable?: boolean;
  isHighlightable?: boolean;
  isExceedable?: boolean;
}

export interface SubBomAttribute {
  _id?: string | null;
  bomTypeId?: string;
  keyName: string;
  type: SubBomAttributeType;
  seqNumber?: number;
  isActive?: boolean;
  textFieldConfig?: SubBomTextFieldConfig | null;
  options: SubBomAttributeOption[];
}

const SERVER_ERROR_CODE = 500;
const GENERIC_ERROR_MESSAGE = 'SUB_BOM_CUSTOM_ATTRIBUTE.SOMETHING_WENT_WRONG';

interface AxiosLikeError {
  response?: { data?: SubBomAttributeEnvelope<unknown> };
}

/**
 * Normalises a rejected request into the same envelope a successful one returns.
 * The endpoints answer 400/404/409 with a real HTTP status, which axios rejects
 * — callers still want the code and message rather than an exception, because
 * the message keys drive the inline validation toasts.
 * @param {unknown} error - The rejection value.
 * @returns {SubBomAttributeEnvelope<unknown>} The error envelope.
 */
function toErrorEnvelope(error: unknown): SubBomAttributeEnvelope<unknown> {
  const envelope = (error as AxiosLikeError)?.response?.data;
  if (envelope && typeof envelope.code === 'number') {
    return { code: envelope.code, message: envelope.message, data: envelope.data ?? null };
  }
  return { code: SERVER_ERROR_CODE, message: GENERIC_ERROR_MESSAGE, data: null };
}

/**
 * Runs one request and resolves with an envelope whether it succeeded or failed.
 * @param {Promise<unknown>} pending - The in-flight request.
 * @returns {Promise<SubBomAttributeEnvelope<TData>>} The success or error envelope.
 */
async function toEnvelope<TData>(pending: Promise<unknown>): Promise<SubBomAttributeEnvelope<TData>> {
  try {
    return (await pending) as unknown as SubBomAttributeEnvelope<TData>;
  } catch (error) {
    return toErrorEnvelope(error) as unknown as SubBomAttributeEnvelope<TData>;
  }
}

/**
 * Fetches the sub-BOM custom attributes defined for one BOM Type.
 * @param {string} bomTypeId - The BOM Type to list.
 * @param {boolean} [isActive] - True to return only live attributes and options.
 * @returns {Promise<SubBomAttributeEnvelope<TData>>} The list envelope.
 */
export function fetchSubBomAttributes<TData>(
  bomTypeId: string,
  isActive = false,
): Promise<SubBomAttributeEnvelope<TData>> {
  return toEnvelope<TData>(
    apiClient.get(`${apiEndpoint}${API.SUB_BOM_CUSTOM_ATTRIBUTE_V2.LIST}`, { params: { bomTypeId, isActive } }),
  );
}

/**
 * Saves the complete attribute list for one BOM Type. The list is sent whole,
 * across all three types, because the server assigns display order from the
 * payload order.
 * @param {string} bomTypeId - The BOM Type being saved.
 * @param {SubBomAttribute[]} attributes - The complete attribute list.
 * @returns {Promise<SubBomAttributeEnvelope<TData>>} The save envelope.
 */
export function saveSubBomAttributes<TData>(
  bomTypeId: string,
  attributes: SubBomAttribute[],
): Promise<SubBomAttributeEnvelope<TData>> {
  return toEnvelope<TData>(
    apiClient.post(`${apiEndpoint}${API.SUB_BOM_CUSTOM_ATTRIBUTE_V2.SAVE}`, { bomTypeId, attributes }),
  );
}

/**
 * Persists a new display order.
 * @param {Array<{ _id: string; sequence: number }>} sequence - Ids paired with their new position.
 * @returns {Promise<SubBomAttributeEnvelope<TData>>} The sequence envelope.
 */
export function updateSubBomAttributeSequence<TData>(
  sequence: Array<{ _id: string; sequence: number }>,
): Promise<SubBomAttributeEnvelope<TData>> {
  return toEnvelope<TData>(
    apiClient.put(`${apiEndpoint}${API.SUB_BOM_CUSTOM_ATTRIBUTE_V2.SEQUENCE}`, { sequence }),
  );
}

/**
 * Switches an attribute, or one of its options, active or inactive.
 * @param {string} attributeId - The attribute to change.
 * @param {boolean} isActive - The state to apply.
 * @param {string | null} [optionId] - The option to change, when narrowing to one.
 * @returns {Promise<SubBomAttributeEnvelope<TData>>} The updated attribute envelope.
 */
export function setSubBomAttributeActive<TData>(
  attributeId: string,
  isActive: boolean,
  optionId: string | null = null,
): Promise<SubBomAttributeEnvelope<TData>> {
  return toEnvelope<TData>(
    apiClient.put(`${apiEndpoint}${API.SUB_BOM_CUSTOM_ATTRIBUTE_V2.DETAIL}${attributeId}`, { isActive, optionId }),
  );
}

/**
 * Soft-deletes an attribute, or just the named options of one.
 * @param {string} attributeId - The attribute to delete, or the option's owner.
 * @param {string[]} [optionIds] - The options to delete, when narrowing to them.
 * @returns {Promise<SubBomAttributeEnvelope<TData>>} The delete envelope.
 */
export function deleteSubBomAttribute<TData>(
  attributeId: string,
  optionIds: string[] = [],
): Promise<SubBomAttributeEnvelope<TData>> {
  return toEnvelope<TData>(
    apiClient.delete(`${apiEndpoint}${API.SUB_BOM_CUSTOM_ATTRIBUTE_V2.DETAIL}${attributeId}`, { data: { optionIds } }),
  );
}
